import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { DateFormats } from 'infrastructure/enums';

import type {
  ITrendingChartApiResponse,
  ITrendingChartPayload,
  ITrendingChartSeriesResponse,
} from 'components/molecules/oneplaform/trending-chart/types';
import type { TFunction } from 'i18next';

const TRENDING_SERIES_ORDER = ['critical', 'at-risk', 'normal'] as const;

type TTrendingSeriesId = (typeof TRENDING_SERIES_ORDER)[number];

type TTrendingLabelKey =
  | 'homeDashboard.trending.critical'
  | 'homeDashboard.trending.atRisk'
  | 'homeDashboard.trending.normal';

type TTrendingSeriesMeta = {
  labelKey: TTrendingLabelKey;
  color: string;
};

const TRENDING_SERIES_META: Record<TTrendingSeriesId, TTrendingSeriesMeta> = {
  critical: {
    labelKey: 'homeDashboard.trending.critical',
    color: 'var(--color-error)',
  },
  'at-risk': {
    labelKey: 'homeDashboard.trending.atRisk',
    color: 'var(--ant-gold-6)',
  },
  normal: {
    labelKey: 'homeDashboard.trending.normal',
    color: 'var(--ant-green-6)',
  },
};

const SERIES_ID_ALIASES: Record<string, TTrendingSeriesId> = {
  risk: 'at-risk',
};

const isSeriesId = (id: string): id is TTrendingSeriesId =>
  TRENDING_SERIES_ORDER.includes(id as TTrendingSeriesId);

const normalizeSeriesId = (
  id?: string | null,
): TTrendingSeriesId | undefined => {
  if (id === undefined || id === null) return undefined;
  const normalized = String(id).trim().toLowerCase();

  if (isSeriesId(normalized)) {
    return normalized;
  }

  return SERIES_ID_ALIASES[normalized];
};

dayjs.extend(utc);
dayjs.extend(timezone);

const sanitizeLabels = (
  labels?: Array<string | { start: string; end: string }>,
): string[] => {
  if (!Array.isArray(labels)) {
    return [];
  }
  return labels.reduce((acc, label) => {
    if (typeof label === 'string') {
      const trimmed = label.trim();
      if (trimmed.length > 0) {
        acc.push(trimmed);
      }
      return acc;
    }
    const { start, end } = label ?? {};
    const startUtc = dayjs.utc(start);
    const endUtc = dayjs.utc(end);
    if (startUtc.isValid() && endUtc.isValid()) {
      const startDate = startUtc.local().format(DateFormats['YYYY-MM-DD']);
      const endDate = endUtc.local().format(DateFormats['YYYY-MM-DD']);
      acc.push(`${startDate} - ${endDate}`);
    }
    return acc;
  }, [] as string[]);
};

const sanitizeValues = (
  values: Array<number | null> | undefined,
  expectedLength: number,
): number[] => {
  if (!Array.isArray(values)) {
    return Array(expectedLength).fill(0);
  }

  const normalized = values.map((value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  });

  if (expectedLength <= 0) {
    return normalized;
  }

  if (normalized.length >= expectedLength) {
    return normalized.slice(0, expectedLength);
  }

  return normalized.concat(Array(expectedLength - normalized.length).fill(0));
};

const getTrendingSeriesMeta = (
  t: TFunction,
): Record<TTrendingSeriesId, { label: string; color: string }> => {
  return TRENDING_SERIES_ORDER.reduce((acc, seriesId) => {
    const meta = TRENDING_SERIES_META[seriesId];
    acc[seriesId] = { label: t(meta.labelKey), color: meta.color };
    return acc;
  }, {} as Record<TTrendingSeriesId, { label: string; color: string }>);
};

export const mapTrendingChartResponse = (
  payload: ITrendingChartApiResponse,
  t: TFunction,
): ITrendingChartPayload => {
  const labels = sanitizeLabels(payload?.labels);
  const datasetMap = new Map<TTrendingSeriesId, number[]>();
  const seriesMeta = getTrendingSeriesMeta(t);

  let resolvedDataSet: ITrendingChartSeriesResponse[] = [];
  if (Array.isArray(payload?.dataSet)) {
    resolvedDataSet = payload.dataSet;
  } else if (Array.isArray(payload?.datasets)) {
    resolvedDataSet = payload.datasets;
  }

  resolvedDataSet.forEach((entry) => {
    const normalizedId = normalizeSeriesId(entry?.id);
    if (!normalizedId) {
      return;
    }

    const normalizedValues = sanitizeValues(entry?.values, labels.length);
    datasetMap.set(normalizedId, normalizedValues);
  });

  const series = TRENDING_SERIES_ORDER.map((seriesId) => {
    const meta = seriesMeta[seriesId];
    const values = datasetMap.get(seriesId) ?? Array(labels.length).fill(0);

    return {
      id: seriesId,
      label: meta.label,
      values,
      color: meta.color,
    };
  });

  return {
    labels,
    series,
  };
};

export const toTimestampParam = (
  date: string,
  boundary: 'start' | 'end',
): number | undefined => {
  if (!date) {
    return undefined;
  }

  const parsed = dayjs(date);

  if (!parsed.isValid()) {
    return undefined;
  }

  return boundary === 'start'
    ? parsed.startOf('day').valueOf()
    : parsed.endOf('day').valueOf();
};
